import { useCallback, useEffect, useSyncExternalStore } from "react";
import { getToken } from "@/lib/api";

/**
 * Shared Server-Sent Events connection.
 *
 * The app should hold exactly one `EventSource` to `/api/sse`, not one per
 * feature. This module owns a singleton connection that is opened lazily on the
 * first `useSseEvent` subscription and closed once the last subscriber unmounts.
 * Multiple components can subscribe to the same or different events; each gets
 * its own listener on the shared stream.
 *
 * #1465: the connection's health is observable too (`useSseStatus`), so a
 * screen that leans on push — the Grid — can tell the reader whether it is
 * live or has fallen back to its polling floor. The browser's EventSource
 * reconnects on its own with backoff; we only report what it is doing.
 */

export type SseStatus = "off" | "connecting" | "live" | "reconnecting";

let source: EventSource | null = null;
let subscriberCount = 0;
let status: SseStatus = "off";
const statusListeners = new Set<() => void>();

function setStatus(next: SseStatus): void {
    if (status === next) return;
    status = next;
    for (const l of statusListeners) l();
}

function ensureSource(): EventSource | null {
    if (source) return source;
    const token = getToken();
    if (!token) return null;
    source = new EventSource(`/api/sse?token=${encodeURIComponent(token)}`);
    setStatus("connecting");
    source.onopen = () => setStatus("live");
    // Do not close on error: EventSource auto-reconnects with backoff, and a
    // shared connection must stay alive for the other subscribers. readyState
    // CLOSED here means the browser gave up (e.g. a 401), which only a fresh
    // subscription cycle can recover from; report it as reconnecting anyway so
    // the reader sees "not live" rather than a stale "live".
    source.onerror = () => setStatus("reconnecting");
    return source;
}

function releaseSource(): void {
    if (subscriberCount > 0) return;
    source?.close();
    source = null;
    setStatus("off");
}

/**
 * Subscribe to a named SSE event for the lifetime of the calling component.
 * `handler` should be stable (wrap in `useCallback`) so it can be cleanly
 * removed and the shared connection ref-counted correctly. Pass `enabled:
 * false` to skip the subscription entirely (and avoid opening the connection)
 * for users who should not receive the event.
 */
export function useSseEvent(event: string, handler: (e: MessageEvent) => void, enabled = true): void {
    useEffect(() => {
        if (!enabled) return;
        const es = ensureSource();
        if (!es) return;
        subscriberCount++;
        es.addEventListener(event, handler as EventListener);
        return () => {
            es.removeEventListener(event, handler as EventListener);
            subscriberCount--;
            releaseSource();
        };
    }, [event, handler, enabled]);
}

function subscribeStatus(cb: () => void): () => void {
    statusListeners.add(cb);
    return () => {
        statusListeners.delete(cb);
    };
}
function getStatus(): SseStatus {
    return status;
}

/** The shared connection's health. "off" until something subscribes. */
export function useSseStatus(): SseStatus {
    return useSyncExternalStore(subscribeStatus, getStatus, getStatus);
}

/**
 * Subscribe to a push event and run `onChange` when it arrives — the common
 * "server says re-read" shape. Same stability rules as `useSseEvent`.
 */
export function useSseInvalidate(event: string, onChange: (data: unknown) => void, enabled = true): void {
    const handler = useCallback(
        (e: MessageEvent) => {
            let data: unknown = null;
            try {
                data = JSON.parse(e.data);
            } catch {
                /* a hint with no body is still a hint */
            }
            onChange(data);
        },
        [onChange],
    );
    useSseEvent(event, handler, enabled);
}
